import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import type { Child } from "@/types";
import { ChildCard } from "./ChildCard";
import { EmptyState } from "@/components/EmptyState";
import { cn } from "@/lib/utils";

interface ChildListProps {
  children: Child[];
  /** Filtir qiymati (animatsiya key uchun) */
  activeFilter: string | number;
  onChildSelect: (child: Child) => void;
  /** Pagination: yana yuklash mumkinmi */
  hasMore?: boolean;
  /** Pagination: "Yana yuklash" bosilganda */
  onLoadMore?: () => void;
  /** Pagination: yuklanmoqda */
  isLoadingMore?: boolean;
  /** Card click/detail yoqilganmi */
  interactive?: boolean;
}

export function ChildList({
  children,
  activeFilter,
  onChildSelect,
  hasMore = false,
  onLoadMore,
  isLoadingMore = false,
  interactive = true,
}: ChildListProps) {
  const { t } = useTranslation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={String(activeFilter)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="flex flex-col gap-4"
      >
        {children.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {children.map((child, index) => (
              <ChildCard
                key={child.id}
                child={child}
                index={index}
                onClick={onChildSelect}
                interactive={interactive}
              />
            ))}
            {hasMore && onLoadMore && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  type="button"
                  onClick={onLoadMore}
                  disabled={isLoadingMore}
                  className={cn(
                    "min-w-[160px] px-6 py-3 rounded-xl text-sm font-semibold transition-all active:scale-[0.98]",
                    "disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100"
                  )}
                  style={{
                    background: "var(--c-elevated-2)",
                    color: "var(--c-text)",
                  }}
                >
                  {isLoadingMore ? (
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-4 w-4 rounded-full animate-spin"
                        style={{
                          border: "2px solid var(--c-divider-strong)",
                          borderTopColor: "var(--c-text)",
                        }}
                      />
                      {t("loading")}
                    </span>
                  ) : (
                    t("loadMore")
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
